import { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnAdmin, isMemberAPracticeManager } from "@/core/auth";
import { useLogout } from "@/hooks/useLogout";
import { useMessages } from "@/hooks/useMessages";
import { useOrganisationQuery } from "@/hooks/useOrganisationQuery";
import { usePermissibleLocationsQuery } from "@/hooks/usePermissibleLocationsQuery";
import { resolveCalendarUrl } from "@/utils/calendarUrl";
import { getInitials, isCurrentSlug } from "@/lib/utils";
import { isOrganisationOwner } from "@/utils/organisationOwner";
import {
  Building2,
  CalendarDays,
  CircleUser,
  EllipsisVertical,
  House,
  // LifeBuoy — restore alongside the commented-out Get Help SidebarMenuItem below
  LogOut,
  MessageSquareMore,
  Settings,
} from "lucide-react";
import { useNavigate } from "react-router";
import Logo from "../Logo/Logo";
import TprIcon from "../TprIcon/TprIcon";
import AdminSecondaryNav from "./AdminSecondaryNav";
import PracticeManagerSecondaryNav from "./PracticeManagerSecondaryNav";
import { useMessagesEnabled } from "@/hooks/useMessagesEnabled";

const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  let navigate = useNavigate();

  const { member } = useMember();
  const logout = useLogout();
  const { isMobile, open, setOpenMobile } = useSidebar();

  // Resolve the org name for org-owner members. `member.organisation` may not always be
  // hydrated on the member record, so we fall back to a lightweight by-id query when it's
  // absent but `ownerOfOrganisationId` is set.
  const isOwner = isOrganisationOwner(member ?? null);
  const orgNameFromMember = member?.organisation?.name;
  const ownerOrgQueryId = isOwner && !orgNameFromMember ? (member?.ownerOfOrganisationId ?? null) : null;
  const { data: ownerOrgData } = useOrganisationQuery(ownerOrgQueryId, { enabled: !!ownerOrgQueryId });
  const orgName = orgNameFromMember ?? ownerOrgData?.name;
  const [isJiggling, setIsJiggling] = useState(false);
  const previousUnreadCountRef = useRef(0);

  // Messaging is feature-flagged. When off we hide the Messages menu entirely and skip the
  // unread fetch (passing "" stops useMessages opening a socket / loading conversations).
  const messagesEnabled = useMessagesEnabled();

  // Get unread message count
  const { unreadCount } = useMessages(messagesEnabled ? member?.id || "" : "");

  const fullName = member ? `${member.firstName} ${member.lastName}` : "User";
  const initials = getInitials(fullName);

  // Determine the calendar URL from the member's RESOLVED permissible locations.
  // `member.permissableLocations` (the array-of-objects field) is null in production for members
  // who only carry `permissableLocationIds`, so reading it sent every such member to the
  // bristol-clifton fallback (locking out anyone without bristol-clifton access — John McLane,
  // 2026-08-03). This query resolves ids → Location objects with real slugs, matching the
  // dashboard grid and the calendar gate.
  const { data: permissibleLocations } = usePermissibleLocationsQuery();
  const calendarUrl = resolveCalendarUrl(permissibleLocations, member?.preferredLocations);

  // Play notification sound and trigger jiggle animation when new message arrives
  useEffect(() => {
    // Skip on initial mount
    if (previousUnreadCountRef.current === 0 && unreadCount > 0) {
      previousUnreadCountRef.current = unreadCount;
      return;
    }

    // Check if unread count increased (new message)
    if (unreadCount > previousUnreadCountRef.current) {
      // Play notification sound
      const audio = new Audio(
        "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBzZ+zO/EiTQJGWi77eiYShcPU6Xk77JbGAg3kdj0xnYnBix+zO/jkDwKF1y17Oeqaw==",
      );
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore if audio fails to play (user hasn't interacted with page yet)
      });

      // Trigger jiggle animation
      setIsJiggling(true);
      setTimeout(() => setIsJiggling(false), 1000);
    }

    previousUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  // Track if Practice Manager and Admin sections are expanded
  const [isPracticeManagerExpanded, setIsPracticeManagerExpanded] = useState(true);
  const [isAdminExpanded, setIsAdminExpanded] = useState(true);

  const clickHandler = (url: string) => {
    if (isMobile) {
      setOpenMobile(false);
    }
    navigate(url);
  };

  return (
    <>
      <div className="max-w-[var(--sidebar-width)] overflow-hidden">
        <Sidebar collapsible="icon" {...props} className={`${open ? "" : "ml-1"}`} data-testid="app-sidebar">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton className="data-[slot=sidebar-menu-button]:!p-1.5 max-w-[var(--sidebar-width)]">
                  {isMobile ? (
                    <>
                      <TprIcon size="small" />
                      <Logo color="black" size="small" />
                    </>
                  ) : (
                    <div className="flex flex-row">
                      <div className="mr-2 w-[25px] -ml-1">
                        <TprIcon size="small" />
                      </div>
                      <Logo color="black" size="small" />
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarContent className="min-h-[80vh]">
            <ScrollArea>
              <SidebarGroup>
                <SidebarGroupContent className="flex flex-col gap-2">
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="Go to Dashboard"
                        className={`hover:cursor-pointer hover:bg-blue-600 hover:text-white ${
                          isCurrentSlug("/dashboard") ? "bg-blue-700 text-white" : ""
                        }`}
                        onClick={() => clickHandler("/dashboard")}
                      >
                        <House />
                        <span>Dashboard</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="Go to Calendar"
                        className={`hover:cursor-pointer hover:bg-blue-600 hover:text-white ${
                          isCurrentSlug("/calendar") || isCurrentSlug("/location") ? "bg-blue-700 text-white" : ""
                        }`}
                        onClick={() => clickHandler(calendarUrl)}
                      >
                        <CalendarDays />
                        <span>Calendar</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {isOwner && orgName && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          tooltip={orgName}
                          className={`min-w-0 hover:cursor-pointer hover:bg-blue-600 hover:text-white ${
                            isCurrentSlug("/organisation") ? "bg-blue-700 text-white" : ""
                          }`}
                          onClick={() => clickHandler("/organisation")}
                          data-testid="sidebar-organisation-item"
                          title={orgName}
                        >
                          <Building2 />
                          {/* min-w-0 lets the flex child shrink so `truncate` actually clips a long
                              org name with an ellipsis instead of pushing the sidebar across the screen. */}
                          <span className="truncate min-w-0">{orgName}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
              {isMemberAPracticeManager(member || null) &&
                member?.practiceManagerLocationIds &&
                member.practiceManagerLocationIds.length > 0 && (
                  <PracticeManagerSecondaryNav
                    handleClick={clickHandler}
                    isExpanded={isPracticeManagerExpanded}
                    onToggle={() => setIsPracticeManagerExpanded(!isPracticeManagerExpanded)}
                  />
                )}
              {isMemberAnAdmin(member || null) && (
                <AdminSecondaryNav
                  handleClick={clickHandler}
                  isExpanded={isAdminExpanded}
                  onToggle={() => setIsAdminExpanded(!isAdminExpanded)}
                />
              )}
            </ScrollArea>
            <div className="mt-auto bg-gray-200 dark:bg-slate-700 rounded-t-md">
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {messagesEnabled && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="Get Help"
                        className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                          isCurrentSlug("/messages") ? "bg-indigo-800 text-white" : ""
                        }`}
                        onClick={() => clickHandler("/messages")}
                      >
                        <div className="flex flex-row items-center justify-between w-full">
                          <div className="flex flex-row items-center">
                            <MessageSquareMore
                              className={`w-4 h-4 mr-2 ${isJiggling ? "animate-[wiggle_0.5s_ease-in-out_2]" : ""}`}
                            />
                            <span>Messages</span>
                          </div>
                          {unreadCount > 0 && (
                            <div>
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="bg-red-700 text-white rounded-full p-0.5 px-2 text-xs font-semibold min-w-[1.5rem] text-center">
                                      {unreadCount > 99 ? "99+" : unreadCount}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent align="start">
                                    You have {unreadCount} unread {unreadCount === 1 ? "message" : "messages"}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    )}
                    {/* Help & Support entry hidden 2026-05-19 — routes (/help) and pages
                        (HelpView, HelpAdminView) remain so this can be re-enabled by deleting
                        the comment wrapper. AppSidebar.test.tsx pins the hidden state. */}
                    {/* <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="Get Help"
                        className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                          isCurrentSlug("/help") ? "bg-indigo-800 text-white" : ""
                        }`}
                        onClick={() => clickHandler("/help")}
                      >
                        <LifeBuoy />
                        <span>Get Help</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem> */}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="Settings"
                        className={`hover:cursor-pointer hover:bg-indigo-600 hover:text-white ${
                          isCurrentSlug("/settings") ? "bg-indigo-800 text-white" : ""
                        }`}
                        onClick={() => clickHandler("/settings")}
                      >
                        <Settings />
                        <span>Settings</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </div>
          </SidebarContent>

          <SidebarFooter className="w-full bg-gray-200 dark:bg-slate-900">
            <SidebarMenu>
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground max-w-[calc(var(--sidebar-width)-2rem)] overflow-hidden"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarImage
                          src={
                            member?.avatar ||
                            "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                          }
                          alt={fullName || "User Avatar"}
                        />
                        <AvatarFallback>{initials}</AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-medium">{fullName}</span>
                        <span className="text-muted-foreground truncate text-xs">{member?.email}</span>
                      </div>
                      <EllipsisVertical />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                    side={isMobile ? "bottom" : "right"}
                    align="end"
                    sideOffset={4}
                  >
                    <DropdownMenuLabel className="p-0 font-normal">
                      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <Avatar className="w-8 h-8">
                          <AvatarImage
                            src={
                              member?.avatar ||
                              "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                            }
                            alt={fullName || "User Avatar"}
                          />
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-medium">{fullName}</span>
                          <span className="text-muted-foreground truncate text-xs">{member?.email}</span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem onClick={() => clickHandler("/account")}>
                        <CircleUser />
                        Account
                      </DropdownMenuItem>
                      {/* <DropdownMenuItem onClick={() => clickHandler("/billing")}>
                        <CreditCard />
                        Billing
                      </DropdownMenuItem> */}
                      {/* <DropdownMenuItem onClick={() => clickHandler("/notifications")}>
                        <BellDot />
                        Notifications
                      </DropdownMenuItem> */}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => logout()}>
                      <LogOut />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
      </div>
    </>
  );
};

export default AppSidebar;
